import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, X } from "lucide-react";
import { api } from "../lib/api";

function highlight(text, q) {
    if (!q || !text) return text;
    const safe = q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const parts = text.split(new RegExp(`(${safe})`, "gi"));
    return parts.map((p, i) =>
        p.toLowerCase() === q.toLowerCase() ? (
            <mark className="hit" key={i}>
                {p}
            </mark>
        ) : (
            <span key={i}>{p}</span>
        ),
    );
}

export default function SearchBar({ compact = false }) {
    const [q, setQ] = useState("");
    const [hits, setHits] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const ref = useRef();
    const timer = useRef();

    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    useEffect(() => {
        if (!q.trim()) {
            setHits([]);
            return;
        }
        clearTimeout(timer.current);
        timer.current = setTimeout(async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/search`, { params: { q: q.trim() } });
                setHits(data);
                setOpen(true);
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        }, 220);
        return () => clearTimeout(timer.current);
    }, [q]);

    const onSelect = (id) => {
        setOpen(false);
        setQ("");
        navigate(`/p/${id}`);
    };

    return (
        <div ref={ref} className={`relative w-full ${compact ? "max-w-xl" : "max-w-2xl"}`}>
            <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                    data-testid="search-input"
                    value={q}
                    onFocus={() => q && setOpen(true)}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search pages: titles, categories, full text…"
                    className={`w-full ${
                        compact ? "h-10" : "h-12"
                    } pl-11 pr-10 bg-white border border-slate-300 rounded-lg shadow-sm text-base focus:outline-none focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-shadow`}
                />
                {loading && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 animate-spin" />
                )}
                {!loading && q && (
                    <button
                        data-testid="search-clear-button"
                        onClick={() => {
                            setQ("");
                            setHits([]);
                        }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100"
                    >
                        <X className="h-3.5 w-3.5 text-slate-400" />
                    </button>
                )}
            </div>
            {open && q && (
                <div
                    data-testid="search-results"
                    className="absolute top-full mt-2 w-full bg-white rounded-lg shadow-xl border border-slate-200 max-h-[65vh] overflow-y-auto z-50 scroll-thin animate-fade-in"
                >
                    {hits.length === 0 && !loading && (
                        <div className="p-6 text-sm text-slate-500" data-testid="search-no-results">
                            No matches for &quot;{q}&quot;.
                        </div>
                    )}
                    <ul>
                        {hits.map((h) => (
                            <li key={h.id} className="border-b border-slate-100 last:border-b-0">
                                <button
                                    data-testid={`search-result-${h.id}`}
                                    onClick={() => onSelect(h.id)}
                                    className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors"
                                >
                                    <div className="flex items-start gap-3">
                                        <span className="font-mono text-[10px] text-slate-400 mt-1 w-10 flex-shrink-0">
                                            p.{String(h.page_number).padStart(3, "0")}
                                        </span>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-0.5">
                                                <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-teal-700 bg-teal-50 px-1.5 py-0.5 rounded">
                                                    {h.category}
                                                </span>
                                                <span className="font-medium text-medical-900 text-sm truncate">
                                                    {highlight(h.title, q)}
                                                </span>
                                            </div>
                                            {h.snippet && (
                                                <div className="text-xs text-slate-600 line-clamp-2 mt-0.5">
                                                    {highlight(h.snippet, q)}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
